import { MissionStatus } from "pages/gantt/typeRenderers/constants";

export interface HumanResource {
    id: number;
    identifier: string;
    firstName: string;
    lastName: string
    missions?: Mission[];
    [key: string]: any;
}

export interface HumanResourceSchedule {
    id: number;
    customFields?: Record<string, any>;
    version: number;
    startTime: number;
    endTime: number;
    humanResourceId: number;
    finished: boolean;
    finishedDate?: number;
    units?: string;
    autoPauseProcessed: boolean;
    shiftName?: string;
    regulator?: string;
    mole?: string;
    driverAssistant1Id?: number;
    driverAssistant2Id?: number;
    humanResource: HumanResource;
    equipment?: {
        id: number;
        [key: string]: any;
    };
    radio?: {
        id: number;
        [key: string]: any;
    };
    driverAssistant1?: {
        id: number;
        [key: string]: any;
    };
    driverAssistant2?: {
        id: number;
        [key: string]: any;
    };
    team?: string;
    teamName?: string;
}

export interface Mission {
    id: number;
    type: {
        id: number;
        color: string;
        name: string
    };
    equipmentResource: {
        id: number;
        identifier: string
    };
    startTime: number;
    endTime: number;
    tasks: Omit<Task, 'mission'>[];
    status: string;
    color: string;
    finishedByUser: boolean;
    inactivityType: {
        id: number;
        name: string;
        color: number
    };
    startAddress: {
        id: number;
        name: string
    };
    endAddress: {
        id: number;
        name: string
    };
    humanResource: HumanResource;
    equipmentType: {
        id: number;
        name: string
    };
    comment: string;
    events?: MissionEvent[];
}

export interface Flight {
    // Basic flight information
    id: number;
    departure: boolean;
    name: string;
    date: number;
    schengen: boolean;
    parking: { id: number; name: string; hotspot: boolean };
    lastKnownDateType: string;
    origin: string;
    destination: string;
    comment: string;
    color: number,
    groundAgent: string,
    passengersNo: number;
    passengerWithDisabilities: string;
    planeIdentifier: string;
    planeType: { id: number; name: string };
    towedStatus: string;
    towedInitialAddress: { id: number; name: string };
    towedRealStartTime: number;
    towedEstimatedStartTime: number;
    towedAddress: { id: number; name: string };
    towedRealEndTime: number;
    rotationFlight: Omit<Flight, 'rotationFlight'>;

    // Icon/UI fields
    priority: boolean;
    importedBy: string;
    verified: boolean;
    modifiedFlight: boolean;
    crewShuttleModified: boolean;
    alreadyEngaged: boolean;
    modifiedObjects: boolean;
    modifiedPlaneIdentifier: boolean;
    alertCreateDefaultObjectsNoReset: boolean;
    orderDate: number;
    inHangar: boolean;
    delayedFlight: boolean;
    parkingChanged: string;
    onHold: boolean;
    tasks: Omit<Task, 'taskGroup'>[];
}

export interface Task {
    id: number;
    taskType: { id: number; color: string; name: string };
    missionType: { id: number; color: string };
    mission: Omit<Mission, 'tasks'>;
    comment: string;
    actualStartTime: number;
    actualEndTime: number;
    taskGroup: Flight;
    name: string;
    quantity: number;
    offloadStatus: string;
    startAddress: { id: number; name: string };
    endAddress: { id: number; name: string };
    requiredEquipmentResourceQualificationType: string;
    equipmentResourceFillPercentage: number;
}

export interface MissionEvent {
    id: number;
    creationdate: number;
    generationSource?: string;
    stopInfo?: string;
    flight?: string;
    objectActionInfo?: string;
    descriptorType?: string;
    deleted?: boolean;
    status?: string;
    notificationCategory?: string;
    driverId?: number;
    driver?: string,
    userName?: string
    message?: string,
    answer?: string,
    statusChangedAt?: number,
    statusChangedBy?: string,
    observation?: string
}

export interface EquipmentResource {
    id: number;
    identifier: string;
    archived: boolean;
    plateNumber: string;
    humanResource: HumanResource;
    equipmentType: {
        id: number;
        name: string
    }
}

export function isMissionFinished(mission: Mission) {
    const { status } = mission;
    if (!status) {
        return false;
    }
    return status == MissionStatus.MISSION_STATUS_FINISHED
        || status == MissionStatus.MISSION_STATUS_TRANSFERED
        || status == MissionStatus.MISSION_STATUS_PLANNED_NOT_DONE
        || status == MissionStatus.MISSION_STATUS_PLANNED_DONE_IN_OTHER_TASK;
}